import { useQuery } from '@tanstack/react-query';
import { USE_REAL_API } from '@/services/api-client';
import * as homeApi from '@/services/home-api';

export type Tone = 'primary' | 'success' | 'warning' | 'secondary';
export type ProgressFill = 'blue' | 'green' | 'amber';
export type OrderStatus = 'active' | 'complete' | 'pending';

export interface KpiCardData {
  id: string;
  labelKey: string;
  value: string;
  unit?: string;
  trendDir: 'up' | 'down';
  trendText: string;
  tone: Tone;
}

export interface QuickActionData {
  id: string;
  icon: string;
  labelKey: string;
  iconTone?: 'amber' | 'green';
  to?: string;
}

export interface OrderRowData {
  id: string;
  customerInitials: string;
  client: string;
  progress: number;
  fill: ProgressFill;
  status: OrderStatus;
  date: string;
}

export interface HomeData {
  kpis: KpiCardData[];
  quickActions: QuickActionData[];
  recentOrders: OrderRowData[];
}

const MOCK: HomeData = {
  kpis: [
    {
      id: 'output',
      labelKey: 'home.kpi_today_output',
      value: '3,842',
      unit: ' pcs',
      trendDir: 'up',
      trendText: '+12.4%',
      tone: 'primary',
    },
    {
      id: 'machines',
      labelKey: 'home.kpi_active_machines',
      value: '18',
      unit: '/24',
      trendDir: 'up',
      trendText: '75% util.',
      tone: 'success',
    },
    {
      id: 'defect',
      labelKey: 'home.kpi_defect_rate',
      value: '2.1',
      unit: '%',
      trendDir: 'down',
      trendText: '-0.3%',
      tone: 'warning',
    },
    {
      id: 'ontime',
      labelKey: 'home.kpi_on_time_rate',
      value: '94',
      unit: '%',
      trendDir: 'up',
      trendText: '+2.1%',
      tone: 'secondary',
    },
  ],
  quickActions: [
    { id: 'new-order', icon: 'add_circle', labelKey: 'home.qa_new_order' },
    { id: 'scan', icon: 'qr_code_scanner', labelKey: 'home.qa_scan', to: '/fa-inventory' },
    { id: 'qc', icon: 'fact_check', labelKey: 'home.qa_qc' },
    { id: 'delivery', icon: 'local_shipping', labelKey: 'home.qa_delivery', iconTone: 'amber' },
    { id: 'inventory', icon: 'inventory_2', labelKey: 'home.qa_inventory', iconTone: 'amber' },
    { id: 'shifts', icon: 'schedule', labelKey: 'home.qa_shifts', iconTone: 'green' },
  ],
  recentOrders: [
    {
      id: 'PO-2026-0891',
      customerInitials: 'NK',
      client: 'NIKE Japan — Screen Print',
      progress: 72,
      fill: 'blue',
      status: 'active',
      date: 'Apr 13',
    },
    {
      id: 'PO-2026-0885',
      customerInitials: 'UL',
      client: 'Uniqlo HK — DTG Print',
      progress: 100,
      fill: 'green',
      status: 'complete',
      date: 'Apr 12',
    },
    {
      id: 'PO-2026-0879',
      customerInitials: 'AD',
      client: 'Adidas — Sublimation',
      progress: 35,
      fill: 'amber',
      status: 'pending',
      date: 'Apr 11',
    },
    {
      id: 'PO-2026-0872',
      customerInitials: 'PM',
      client: 'Puma EU — Heat Transfer',
      progress: 58,
      fill: 'blue',
      status: 'active',
      date: 'Apr 10',
    },
  ],
};

export function useHomeData() {
  return useQuery({
    queryKey: ['home', USE_REAL_API],
    queryFn: async (): Promise<HomeData> => {
      if (USE_REAL_API) return homeApi.fetchHomeData();
      await new Promise((r) => setTimeout(r, 120));
      return MOCK;
    },
    staleTime: 30_000,
  });
}
